import React from 'react';
import { View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { cn } from '@/utils/cn';

interface Props {
  title: string;
  description: string;
  /** Exemplo curto do que o cliente veria — âncora concreta da escolha. */
  example?: string;
  selected: boolean;
  onPress: () => void;
  testID?: string;
}

/**
 * Escolha grande e única: um card por opção, marcador de rádio à esquerda.
 * Usado onde a decisão muda o resto do formulário e precisa ser lida com calma.
 */
export function ChoiceCard({
  title,
  description,
  example,
  selected,
  onPress,
  testID,
}: Props) {
  return (
    <PressableScale
      haptic
      accessibilityRole="radio"
      accessibilityState={{ selected }}
      onPress={onPress}
      testID={testID}
      className={cn(
        'flex-row gap-3 rounded-2xl border p-4',
        selected ? 'border-primary bg-primary/5' : 'border-neutral-900/5 bg-card',
      )}
      style={{ borderCurve: 'continuous' }}
    >
      <View
        className={cn(
          'mt-0.5 h-5 w-5 items-center justify-center rounded-full border-2',
          selected ? 'border-primary bg-primary' : 'border-neutral-300',
        )}
      >
        {selected ? <View className="h-1.5 w-1.5 rounded-full bg-white" /> : null}
      </View>

      <View className="flex-1 gap-1">
        <ThemedText className="font-semibold text-[15px] leading-5 text-neutral-900">
          {title}
        </ThemedText>
        <ThemedText className="text-xs leading-4 text-neutral-500">
          {description}
        </ThemedText>
        {example ? (
          <View
            className={cn(
              'mt-1.5 flex-row items-center gap-1.5 self-start rounded-lg px-2.5 py-1',
              selected ? 'bg-card' : 'bg-neutral-100',
            )}
          >
            <ThemedText className="text-[11px] font-medium text-neutral-500">
              Exemplo:
            </ThemedText>
            <ThemedText className="text-[11px] font-semibold text-primary">
              {example}
            </ThemedText>
          </View>
        ) : null}
      </View>
    </PressableScale>
  );
}
